import { useClerk, useOrganizationList } from "@clerk/clerk-react";
import type { ActiveOrg } from "@/schema";
import {
  Boxes,
  Building2,
  Plug,
  Plus,
  UserPlus,
  Users,
} from "lucide-react";
import type React from "react";
import { useEffect, useRef, useState } from "react";

import type { ConversationFeedItem } from "../channel/client";
import {
  EMPTY_TAB_SLICE,
  feedStore,
  useFeedStoreState,
} from "../channel/feedStore";
import type { ChannelApi } from "../channel/useChannel";
import { activeContextStore } from "../services/ActiveContextStore";
import { SourcesSection } from "./SourcesSection";

type ManagementTab = "integrations" | "entities" | "members" | "organizations";
type EntityChildKind = "surface" | "file";
type ProfileChannel = Pick<ChannelApi, "ready" | "query" | "hydrateArtifact">;

interface ProfileManagementSectionProps {
  activeOrg: ActiveOrg;
  channel: ProfileChannel;
}

interface MemberSummary {
  id: string;
  name: string;
  email: string;
  imageUrl?: string;
  role: string;
}

interface EntityChildSummary {
  kind: EntityChildKind;
  machine: string;
  label: string;
  server: string;
}

interface EntitySummary {
  id: string;
  label: string;
  children: EntityChildSummary[];
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const nonEmptyString = (value: unknown): string =>
  typeof value === "string" ? value.trim() : "";

const entitySummaryOf = (
  item: ConversationFeedItem,
  hydratedArtifact: unknown,
): EntitySummary => {
  if (!isRecord(hydratedArtifact)) {
    throw new Error(`Entity ${item.id} did not hydrate to an artifact.`);
  }
  const input = isRecord(hydratedArtifact.input) ? hydratedArtifact.input : null;
  const entity = input && isRecord(input.entity) ? input.entity : null;
  if (!entity) {
    throw new Error(`Entity ${item.id} is missing artifact.input.entity.`);
  }
  const machine = isRecord(entity.machine) ? entity.machine : null;
  const id = nonEmptyString(machine?.id);
  if (!id) {
    throw new Error(`Entity ${item.id} is missing its declared machine id.`);
  }
  if (!Array.isArray(entity.children)) {
    throw new Error(`Entity ${id} is missing its declared children.`);
  }

  const children = entity.children.map((value, index): EntityChildSummary => {
    if (!isRecord(value)) {
      throw new Error(`Entity ${id} child ${index + 1} is not an object.`);
    }
    const kind = nonEmptyString(value.kind);
    if (kind !== "surface" && kind !== "file") {
      throw new Error(`Entity ${id} child ${index + 1} has undeclared kind "${kind}".`);
    }
    const childMachine = nonEmptyString(value.machine);
    if (!childMachine) {
      throw new Error(`Entity ${id} ${kind} child ${index + 1} is missing its machine.`);
    }
    return {
      kind,
      machine: childMachine,
      label: nonEmptyString(value.label) || childMachine,
      server: nonEmptyString(value.server),
    };
  });

  return {
    id,
    label: nonEmptyString(machine?.label) || id,
    children,
  };
};

const mergeEntities = (values: EntitySummary[]): EntitySummary[] => {
  const byId = new Map<string, EntitySummary>();
  for (const value of values) {
    const held = byId.get(value.id);
    const children = [...(held?.children || []), ...value.children];
    const uniqueChildren = Array.from(
      new Map(
        children.map((child) => [`${child.kind}:${child.machine}`, child]),
      ).values(),
    ).sort((left, right) =>
      left.label.localeCompare(right.label) ||
      left.machine.localeCompare(right.machine),
    );
    byId.set(value.id, {
      id: value.id,
      label: value.label || held?.label || value.id,
      children: uniqueChildren,
    });
  }
  return Array.from(byId.values()).sort((left, right) =>
    left.label.localeCompare(right.label) || left.id.localeCompare(right.id),
  );
};

const formatRole = (role?: string | null) =>
  String(role || "member").replace(/^org:/, "").replace(/_/g, " ");

export const ProfileManagementSection = ({
  activeOrg,
  channel,
}: ProfileManagementSectionProps) => {
  const clerk = useClerk();
  const context = activeContextStore.read();
  const organizationOptions = context?.options.orgs.items || [];
  const ownerOrganizationId = activeOrg.parentOrgId || activeOrg.id;
  const ownerOrganization =
    organizationOptions.find(({ id }) => id === ownerOrganizationId) || activeOrg;
  const hasTenantContext =
    Boolean(activeOrg.managed && activeOrg.parentOrgId) ||
    organizationOptions.some(
      (organization) =>
        organization.managed === true &&
        organization.parentOrgId === activeOrg.id,
    );

  const organizationsById = new Map<string, ActiveOrg>();
  organizationOptions
    .filter(
      (organization) =>
        organization.id === ownerOrganizationId ||
        organization.parentOrgId === ownerOrganizationId,
    )
    .forEach((organization) => organizationsById.set(organization.id, organization));
  organizationsById.set(activeOrg.id, {
    ...organizationsById.get(activeOrg.id),
    ...activeOrg,
  });
  const organizations = Array.from(organizationsById.values());

  const tabs = [
    { id: "integrations" as const, label: "Integrations", Icon: Plug },
    { id: "entities" as const, label: "Entities", Icon: Boxes },
    { id: "members" as const, label: "Members", Icon: Users },
    ...(hasTenantContext
      ? [{ id: "organizations" as const, label: "Organizations", Icon: Building2 }]
      : []),
  ];

  const activeUser = context?.user;
  const currentMemberName =
    activeUser?.fullName?.trim() ||
    [activeUser?.firstName, activeUser?.lastName].filter(Boolean).join(" ").trim() ||
    activeUser?.name?.trim() ||
    activeUser?.email?.split("@")[0] ||
    String(activeUser?.id || "");

  const [activeTab, setActiveTab] = useState<ManagementTab>("integrations");
  const [showAddMenu, setShowAddMenu] = useState(false);
  const [integrationAddRequest, setIntegrationAddRequest] = useState(0);
  const [actionError, setActionError] = useState<string | null>(null);
  const addMenuRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (activeTab === "organizations" && !hasTenantContext) {
      setActiveTab("integrations");
    }
  }, [activeTab, hasTenantContext]);

  useEffect(() => {
    if (!showAddMenu) return;
    const closeOnOutsideClick = (event: MouseEvent) => {
      if (addMenuRef.current && !addMenuRef.current.contains(event.target as Node)) {
        setShowAddMenu(false);
      }
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setShowAddMenu(false);
    };
    document.addEventListener("mousedown", closeOnOutsideClick);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("mousedown", closeOnOutsideClick);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [showAddMenu]);

  const openMemberManager = async () => {
    setShowAddMenu(false);
    setActionError(null);
    try {
      if (clerk.organization?.id !== ownerOrganizationId) {
        await clerk.setActive({ organization: ownerOrganizationId });
      }
      clerk.openOrganizationProfile({
        __experimental_startPath: "/organization-members",
      });
    } catch (error: unknown) {
      setActionError(
        error instanceof Error
          ? error.message
          : "Member management could not open for this organization.",
      );
    }
  };

  const openOrganizationCreator = () => {
    setShowAddMenu(false);
    setActionError(null);
    clerk.openCreateOrganization({
      hideSlug: true,
      skipInvitationScreen: true,
      afterCreateOrganizationUrl: (organization) => {
        activeContextStore.queueTenantAttachment({
          id: organization.id,
          name: organization.name,
        });
        return "/";
      },
    });
  };

  return (
    <div style={sectionCard} data-profile-management>
      <div style={toolbar}>
        <div role="tablist" aria-label="Profile management" style={tabList}>
          {tabs.map(({ id, label, Icon }) => {
            const selected = activeTab === id;
            return (
              <button
                key={id}
                id={`profile-management-tab-${id}`}
                type="button"
                role="tab"
                data-profile-tab={id}
                aria-selected={selected}
                aria-controls={`profile-management-panel-${id}`}
                tabIndex={selected ? 0 : -1}
                onClick={() => {
                  setShowAddMenu(false);
                  setActionError(null);
                  setActiveTab(id);
                }}
                style={{
                  ...tabButton,
                  background: selected ? "var(--color-bg)" : "transparent",
                  color: selected ? "var(--color-text)" : "var(--color-text-secondary)",
                }}
              >
                <Icon size={13} />
                <span>{label}</span>
              </button>
            );
          })}
        </div>

        <div ref={addMenuRef} style={{ position: "relative", flexShrink: 0 }}>
          <button
            type="button"
            onClick={() => setShowAddMenu((open) => !open)}
            title="Add integration, member, or organization"
            aria-label="Add integration, member, or organization"
            aria-haspopup="menu"
            aria-expanded={showAddMenu}
            style={{
              ...addButton,
              background: showAddMenu ? "var(--color-bg)" : "transparent",
            }}
          >
            <Plus size={14} />
          </button>
          {showAddMenu && (
            <div role="menu" aria-label="Profile add menu" style={addMenu}>
              <button
                type="button"
                role="menuitem"
                style={addMenuItem}
                onClick={() => {
                  setShowAddMenu(false);
                  setActionError(null);
                  setActiveTab("integrations");
                  setIntegrationAddRequest((request) => request + 1);
                }}
              >
                <Plug size={13} style={menuIcon} />
                <span>Add integration</span>
              </button>
              <button
                type="button"
                role="menuitem"
                style={addMenuItem}
                onClick={() => void openMemberManager()}
              >
                <UserPlus size={13} style={menuIcon} />
                <span>Add member</span>
              </button>
              <button
                type="button"
                role="menuitem"
                style={addMenuItem}
                onClick={openOrganizationCreator}
              >
                <Building2 size={13} style={menuIcon} />
                <span>New organization</span>
              </button>
            </div>
          )}
        </div>
      </div>

      {actionError && (
        <div role="alert" style={actionErrorStyle}>
          {actionError}
        </div>
      )}

      {activeTab === "integrations" && (
        <div
          id="profile-management-panel-integrations"
          role="tabpanel"
          aria-labelledby="profile-management-tab-integrations"
          data-profile-panel="integrations"
        >
          <SourcesSection
            mode="all"
            showHeader={false}
            embedded
            addRequestKey={integrationAddRequest}
          />
        </div>
      )}

      {activeTab === "entities" && (
        <EntitiesPanel channel={channel} />
      )}

      {activeTab === "members" && (
        <div
          id="profile-management-panel-members"
          role="tabpanel"
          aria-labelledby="profile-management-tab-members"
          data-profile-panel="members"
          style={panel}
        >
          <MembersList
            ownerOrganizationId={ownerOrganizationId}
            organizationName={ownerOrganization.name || ownerOrganization.id}
            providerSignedIn={Boolean(clerk.isSignedIn)}
            currentMember={{
              id: String(activeUser?.id || "current-member"),
              name: currentMemberName || "Current member",
              email: activeUser?.email || "",
              role: formatRole(activeOrg.orgRole),
            }}
          />
        </div>
      )}

      {activeTab === "organizations" && hasTenantContext && (
        <div
          id="profile-management-panel-organizations"
          role="tabpanel"
          aria-labelledby="profile-management-tab-organizations"
          data-profile-panel="organizations"
          style={panel}
        >
          <div role="list" aria-label="Available organizations" style={list}>
            {organizations.map((organization) => (
              <div key={organization.id} role="listitem" style={row}>
                <div style={organizationIcon}>
                  <Building2 size={14} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={primaryText}>{organization.name || organization.id}</div>
                  <div style={secondaryText}>
                    {formatRole(organization.orgRole)}
                    {organization.managed ? " · managed tenant" : ""}
                  </div>
                </div>
                {organization.id === activeOrg.id && (
                  <span style={badge}>Current</span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const EntitiesPanel = ({ channel }: { channel: ProfileChannel }) => {
  const feed = useFeedStoreState();
  const book = feed.byTab.book || EMPTY_TAB_SLICE;
  const [entities, setEntities] = useState<EntitySummary[]>([]);
  const [hydrating, setHydrating] = useState(false);
  const [hydrationError, setHydrationError] = useState("");
  const channelReady = channel.ready;
  const channelQuery = channel.query;
  const hydrateArtifact = channel.hydrateArtifact;

  useEffect(() => {
    if (!channelReady) return;
    void feedStore.ensureTab("book", channelQuery);
  }, [channelQuery, channelReady]);

  useEffect(() => {
    if (book.items.length === 0) {
      setEntities([]);
      setHydrating(false);
      setHydrationError("");
      return;
    }

    let cancelled = false;
    setHydrating(true);
    setHydrationError("");
    void Promise.all(
      book.items.map(async (item) => {
        if (!item.artifact) {
          throw new Error(`Entity ${item.id} is missing its artifact pointer.`);
        }
        const hydrated = await hydrateArtifact(item.artifact);
        return entitySummaryOf(item, hydrated);
      }),
    )
      .then((summaries) => {
        if (cancelled) return;
        setEntities(mergeEntities(summaries));
        setHydrating(false);
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        setHydrating(false);
        setHydrationError(
          error instanceof Error
            ? error.message
            : "Entities could not be hydrated.",
        );
      });

    return () => {
      cancelled = true;
    };
  }, [book.items, hydrateArtifact]);

  const error = book.error || hydrationError;
  const loading =
    entities.length === 0 &&
    !error &&
    (!channelReady || book.loading || !book.loaded || hydrating);

  return (
    <div
      id="profile-management-panel-entities"
      role="tabpanel"
      aria-labelledby="profile-management-tab-entities"
      aria-busy={book.loading || hydrating}
      data-profile-panel="entities"
      style={panel}
    >
      {error && (
        <div role="alert" style={inlineError} data-entity-state="error">
          {error}
        </div>
      )}
      {loading && (
        <div role="status" style={emptyState} data-entity-state="loading">
          Loading entities…
        </div>
      )}
      {!loading && !error && entities.length === 0 && (
        <div role="status" style={emptyState} data-entity-state="empty">
          No entities yet. Entities appear when a file or surface crosses.
        </div>
      )}
      {entities.length > 0 && (
        <div role="list" aria-label="Managed entities" style={entityList}>
          {entities.map((entity) => (
            <EntityCard key={entity.id} entity={entity} />
          ))}
        </div>
      )}
    </div>
  );
};

const EntityCard = ({ entity }: { entity: EntitySummary }) => {
  const groups: Array<{ kind: EntityChildKind; label: string }> = [
    { kind: "surface", label: "Surfaces" },
    { kind: "file", label: "Files" },
  ];

  return (
    <article role="listitem" data-entity-id={entity.id} style={entityCard}>
      <div style={entityHeader}>
        <div style={entityIcon}>
          <Boxes size={14} aria-hidden="true" />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={primaryText} title={entity.label}>
            {entity.label}
          </div>
          <div style={secondaryText}>
            {entity.children.length} {entity.children.length === 1 ? "source" : "sources"}
          </div>
        </div>
      </div>

      <div style={entityGroups}>
        {groups.map(({ kind, label }) => {
          const children = entity.children.filter((child) => child.kind === kind);
          if (children.length === 0) return null;
          const visibleChildren = children.slice(0, 3);
          return (
            <section key={kind} data-entity-fill-kind={kind} style={entityGroup}>
              <div style={entityGroupLabel}>
                <span>{label}</span>
                <span style={entityGroupCount}>{children.length}</span>
              </div>
              <div
                role="list"
                aria-label={`${entity.label} ${label.toLowerCase()}`}
                style={entityChildren}
              >
                {visibleChildren.map((child) => (
                  <div
                    key={`${child.kind}:${child.machine}`}
                    role="listitem"
                    data-entity-child-machine={child.machine}
                    style={entityChild}
                    title={child.label}
                  >
                    <div style={entityChildLabel}>{child.label}</div>
                    {child.server && (
                      <div style={entityChildServer}>{child.server}</div>
                    )}
                  </div>
                ))}
                {children.length > visibleChildren.length && (
                  <div style={entityMore}>
                    +{children.length - visibleChildren.length} more
                  </div>
                )}
              </div>
            </section>
          );
        })}
        {entity.children.length === 0 && (
          <div style={entityNoSources}>No file or surface sources.</div>
        )}
      </div>
    </article>
  );
};

const MembersList = ({
  ownerOrganizationId,
  organizationName,
  providerSignedIn,
  currentMember,
}: {
  ownerOrganizationId: string;
  organizationName: string;
  providerSignedIn: boolean;
  currentMember: MemberSummary;
}) => {
  const { isLoaded, userMemberships } = useOrganizationList({
    userMemberships: {
      infinite: true,
      pageSize: 100,
    },
  });
  const [members, setMembers] = useState<MemberSummary[]>([]);
  const [loading, setLoading] = useState(providerSignedIn);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!providerSignedIn) {
      setMembers([currentMember]);
      setLoading(false);
      setError(null);
      return;
    }
    if (!isLoaded || userMemberships.isLoading) {
      setLoading(true);
      return;
    }

    const ownerMembership = userMemberships.data?.find(
      (membership) => membership.organization.id === ownerOrganizationId,
    );
    if (!ownerMembership) {
      setMembers([]);
      setLoading(false);
      setError("Member directory is unavailable for this organization.");
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);
    void ownerMembership.organization
      .getMemberships({ pageSize: 100 })
      .then((response) => {
        if (cancelled) return;
        setMembers(
          response.data.map((membership) => {
            const member = membership.publicUserData;
            return {
              id: member?.userId || membership.id,
              name:
                [member?.firstName, member?.lastName].filter(Boolean).join(" ").trim() ||
                member?.identifier ||
                membership.id,
              email: member?.identifier || "",
              imageUrl: member?.imageUrl,
              role: formatRole(membership.roleName || membership.role),
            };
          }),
        );
        setLoading(false);
      })
      .catch((requestError: unknown) => {
        if (cancelled) return;
        setMembers([]);
        setLoading(false);
        setError(
          requestError instanceof Error
            ? requestError.message
            : "Member directory could not be loaded.",
        );
      });

    return () => {
      cancelled = true;
    };
  }, [
    currentMember,
    isLoaded,
    ownerOrganizationId,
    providerSignedIn,
    userMemberships.data,
    userMemberships.isLoading,
  ]);

  return (
    <>
      <div style={panelLabel}>{organizationName}</div>
      <div role="list" aria-label="Organization members" style={list}>
        {loading && <div style={emptyState}>Loading members…</div>}
        {!loading && error && (
          <div role="alert" style={inlineError}>
            {error}
          </div>
        )}
        {!loading && !error && members.length === 0 && (
          <div style={emptyState}>No members found.</div>
        )}
        {!loading &&
          !error &&
          members.map((member) => (
            <div key={member.id} role="listitem" style={row}>
              <div style={memberAvatar}>
                {member.imageUrl ? (
                  <img src={member.imageUrl} alt="" style={memberAvatarImage} />
                ) : (
                  <span>{member.name.slice(0, 1).toUpperCase()}</span>
                )}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={primaryText}>{member.name}</div>
                <div style={secondaryText}>{member.email}</div>
              </div>
              <span style={badge}>{member.role}</span>
            </div>
          ))}
      </div>
    </>
  );
};

const sectionCard: React.CSSProperties = {
  background: "var(--color-surface)",
  border: "1px solid var(--color-border)",
  borderRadius: 10,
  padding: "12px 12px 14px",
};

const toolbar: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  marginBottom: 12,
};

const tabList: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 2,
  flex: 1,
  minWidth: 0,
};

const tabButton: React.CSSProperties = {
  minWidth: 0,
  height: 28,
  padding: "0 8px",
  borderRadius: 6,
  border: "none",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 5,
  fontSize: 11,
  fontWeight: 600,
  cursor: "pointer",
  whiteSpace: "nowrap",
};

const addButton: React.CSSProperties = {
  width: 28,
  height: 28,
  borderRadius: 6,
  border: "none",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  color: "var(--color-text-secondary)",
  cursor: "pointer",
};

const addMenu: React.CSSProperties = {
  position: "absolute",
  right: 0,
  top: "calc(100% + 6px)",
  zIndex: 60,
  minWidth: 180,
  padding: "4px 0",
  overflow: "hidden",
  background: "var(--color-surface)",
  border: "1px solid var(--color-border)",
  borderRadius: 8,
  boxShadow: "0 4px 16px rgba(0,0,0,0.2)",
};

const addMenuItem: React.CSSProperties = {
  width: "100%",
  display: "flex",
  alignItems: "center",
  gap: 8,
  padding: "8px 12px",
  border: "none",
  background: "transparent",
  cursor: "pointer",
  fontSize: 13,
  color: "var(--color-text)",
  textAlign: "left",
};

const menuIcon: React.CSSProperties = {
  flexShrink: 0,
  color: "var(--color-text-secondary)",
};

const panel: React.CSSProperties = {
  height: 214,
  overflowY: "auto",
  overflowX: "hidden",
};

const panelLabel: React.CSSProperties = {
  margin: "0 2px 8px",
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: "0.04em",
  textTransform: "uppercase",
  color: "var(--color-text-secondary)",
};

const list: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 7,
};

const entityList: React.CSSProperties = {
  ...list,
  gap: 8,
};

const entityCard: React.CSSProperties = {
  padding: 9,
  border: "1px solid var(--color-border)",
  borderRadius: 8,
  background: "var(--color-bg)",
};

const entityHeader: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 8,
};

const entityIcon: React.CSSProperties = {
  width: 28,
  height: 28,
  borderRadius: 7,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
  color: "var(--color-text-secondary)",
  background: "var(--color-surface)",
};

const entityGroups: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
  gap: 7,
  marginTop: 8,
};

const entityGroup: React.CSSProperties = {
  minWidth: 0,
  padding: "7px 8px",
  borderRadius: 7,
  background: "var(--color-surface)",
};

const entityGroupLabel: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 8,
  marginBottom: 5,
  color: "var(--color-text-secondary)",
  fontSize: 10,
  fontWeight: 700,
  letterSpacing: "0.04em",
  textTransform: "uppercase",
};

const entityGroupCount: React.CSSProperties = {
  fontSize: 9,
  fontVariantNumeric: "tabular-nums",
};

const entityChildren: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 4,
};

const entityChild: React.CSSProperties = {
  minWidth: 0,
};

const entityChildLabel: React.CSSProperties = {
  overflow: "hidden",
  color: "var(--color-text)",
  fontSize: 11,
  fontWeight: 500,
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

const entityChildServer: React.CSSProperties = {
  overflow: "hidden",
  marginTop: 1,
  color: "var(--color-text-secondary)",
  fontSize: 10,
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

const entityMore: React.CSSProperties = {
  color: "var(--color-text-secondary)",
  fontSize: 10,
};

const entityNoSources: React.CSSProperties = {
  color: "var(--color-text-secondary)",
  fontSize: 11,
};

const row: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 9,
  padding: "8px 9px",
  border: "1px solid var(--color-border)",
  borderRadius: 7,
  background: "var(--color-bg)",
};

const primaryText: React.CSSProperties = {
  fontSize: 12,
  fontWeight: 600,
  color: "var(--color-text)",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

const secondaryText: React.CSSProperties = {
  marginTop: 1,
  fontSize: 11,
  color: "var(--color-text-secondary)",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

const organizationIcon: React.CSSProperties = {
  width: 28,
  height: 28,
  borderRadius: 7,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: "var(--color-text-secondary)",
  background: "var(--color-surface)",
  flexShrink: 0,
};

const memberAvatar: React.CSSProperties = {
  ...organizationIcon,
  borderRadius: "50%",
  overflow: "hidden",
  fontSize: 10,
  fontWeight: 700,
};

const memberAvatarImage: React.CSSProperties = {
  width: "100%",
  height: "100%",
  objectFit: "cover",
};

const badge: React.CSSProperties = {
  padding: "2px 6px",
  borderRadius: 99,
  background: "var(--color-surface)",
  border: "1px solid var(--color-border)",
  color: "var(--color-text-secondary)",
  fontSize: 10,
  fontWeight: 600,
  flexShrink: 0,
};

const emptyState: React.CSSProperties = {
  padding: "12px 8px",
  color: "var(--color-text-secondary)",
  fontSize: 12,
};

const inlineError: React.CSSProperties = {
  ...emptyState,
  color: "var(--status-danger-text)",
};

const actionErrorStyle: React.CSSProperties = {
  marginBottom: 10,
  padding: "8px 10px",
  borderRadius: 8,
  border: "1px solid var(--status-danger-border)",
  background: "var(--status-danger-bg)",
  color: "var(--status-danger-text)",
  fontSize: 12,
};
